import React, { memo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { localeCodeOf } from '../../../../generic/utilities/customLanguage'

/**
 * Renders the time axis (X-axis) for the timeline.
 * @param {object} props
 * @param {moment.Moment} props.windowStart - Start of the time window.
 * @param {moment.Moment} props.windowEnd - End of the time window.
 */
const TimeAxis = ({ windowStart, windowEnd }) => {
  const theme = useTheme()
  const { i18n } = useTranslation()
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))
  const isVeryNarrow = useMediaQuery('(max-width:420px)')
  const axisRef = useRef(null)
  const [axisWidth, setAxisWidth] = useState(0)

  useEffect(() => {
    if (!axisRef.current) return
    const resizeObserver = new window.ResizeObserver((entries) => {
      for (const entry of entries) {
        setAxisWidth(entry.contentRect.width)
      }
    })
    resizeObserver.observe(axisRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  const mapLanguageToLocale = (lang) => {
    switch (lang) {
      case 'es':
        return 'es-ES'
      case 'en':
        return 'en-GB'
      case 'pt':
        return 'pt-PT'
      case 'fr':
        return 'fr-FR'
      case 'ca':
        return 'ca-ES'
      case 'eus':
        return 'eu-ES'
      case 'lv':
        return 'lv-LV'
      case 'de':
        return 'de-DE'
      default:
        return 'en-GB'
    }
  }

  const uiLocale = mapLanguageToLocale(localeCodeOf(i18n?.language))

  const formatLabelDynamic = (ts, rangeMs) => {
    if (!ts) return ''
    const d = new Date(ts)
    if (rangeMs <= 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { hour: 'numeric', minute: '2-digit', hour12: false }).format(d)
    }
    if (rangeMs <= 3 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: false }).format(d)
    }
    if (rangeMs <= 14 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: false }).format(d)
    }
    if (rangeMs <= 60 * 24 * 60 * 60 * 1000) {
      return new Intl.DateTimeFormat(uiLocale, { month: 'short', day: '2-digit' }).format(d)
    }
    return d.toLocaleDateString('en-CA')
  }

  const hasWindow = !!windowStart && !!windowEnd && moment(windowEnd).isAfter(windowStart)
  const startMs = hasWindow ? windowStart.valueOf() : 0
  const endMs = hasWindow ? windowEnd.valueOf() : 0
  const windowMs = hasWindow ? Math.max(0, endMs - startMs) : 0
  const segments = 12

  let majorStep = 3
  if (axisWidth > 0) {
    if (axisWidth < 520) majorStep = 4
    if (axisWidth < 420) majorStep = 6
  } else {
    if (isNarrow) majorStep = 4
    if (isVeryNarrow) majorStep = 6
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, position: 'relative', height: 44 }}>
      <Box sx={{ width: 120, minWidth: 120 }} /> {/* Spacer for labels */}
      <Box ref={axisRef} sx={{ flexGrow: 1, position: 'relative', height: '100%' }}>
        <Box
          component='svg'
          role='img'
          aria-label='Eje de tiempo'
          viewBox='0 0 1000 44'
          preserveAspectRatio='none'
          sx={{ width: '100%', height: '100%', display: 'block' }}
        >
          <g shapeRendering='crispEdges'>
            <line
              x1='0'
              y1='10'
              x2='1000'
              y2='10'
              stroke={theme.palette.text.secondary}
              strokeWidth='1'
              opacity='0.7'
            />
            {Array.from({ length: segments + 1 }).map((_, i) => {
              const x = (i / segments) * 1000
              const isMajor = i % majorStep === 0 || i === segments
              const tickLen = isMajor ? 10 : 6
              const tickOpacity = isMajor ? 0.7 : 0.4
              const ts = hasWindow ? startMs + Math.round((i / segments) * windowMs) : null
              const label = hasWindow && isMajor ? formatLabelDynamic(ts, windowMs) : ''
              const textAnchor = i === 0 ? 'start' : (i === segments ? 'end' : 'middle')

              return (
                <g key={`timeTick${i}`}>
                  <line
                    x1={x}
                    y1='10'
                    x2={x}
                    y2={10 + tickLen}
                    stroke={theme.palette.text.secondary}
                    strokeWidth='1'
                    opacity={tickOpacity}
                  />
                  {label
                    ? (
                      <text
                        x={x}
                        y='38'
                        fill={theme.palette.text.secondary}
                        fontSize='11'
                        fontWeight='600'
                        textAnchor={textAnchor}
                        style={{ userSelect: 'none', fontFamily: 'Roboto, sans-serif' }}
                      >
                        {label}
                      </text>
                      )
                    : null}
                </g>
              )
            })}
          </g>
        </Box>
      </Box>
    </Box>
  )
}

TimeAxis.propTypes = {
  windowStart: PropTypes.object.isRequired,
  windowEnd: PropTypes.object.isRequired
}

export default memo(TimeAxis)
